// A2-582 — the answers drawn above a card's rows: what every open row of the
// group takes (g.bulk_answers, all pages — so "all N rows" is the server's
// count, not this page's), and the bar that gives one answer to the ticked
// rows. Every control here is chosen from what the server sent, never from
// the card's flag name (answers.js).
import React from "react";
import { useTranslation } from "react-i18next";
import { RateCategoryAction } from "./RateCategoryAction";
import { FamilyBBulkResolver } from "./FamilyBBulkResolver";
import { RefundChannelResolver } from "./RefundChannelResolver";
import { RefundChannelAction } from "./RefundChannelAction";
import { CustomerEstablishmentResolver } from "./CustomerEstablishmentResolver";
import { PropertyLettingBulk } from "./PropertyLettingResolver";
import { LocationCountryPicker } from "./LocationCountryPicker";
import { OnlineEventVatChoiceCta } from "./OnlineEventVatChoiceCta";
import { AddRegistrationCta } from "../AddRegistrationCta";
import { GoToAnswer, FixInFileAnswer, NoWayOutAnswer } from "./AnswerNotes";
import { LeaveOutCopy } from "./LeaveOutCopy";
import { CustomerCountry, SoldAs } from "./CustomerAnswers";
import { BusinessEvidence } from "./BusinessEvidence";
import { ClearDecision } from "./ClearDecision";
import { groupAnswer, rowTakes } from "./answers";

export const GroupAnswers = ({
  g, calcId, clientId, calcFrozen, data, bulkSel, setBulkSel, setShowRows, onDone,
}) => {
  const { t } = useTranslation();
  const rows = g.rows || [];
  const open = (r) => !(g.acknowledged_txn_ids || []).includes(r.txn_id);
  // A2-637 Owner A: every answer on this card's rows and in its shared list
  // is the card's own (the server's card_answers / bulk_answers); this file
  // draws them, it chooses none.
  const takers = (kind, extra) => rows.filter((r) => open(r) && rowTakes(r, g, kind, extra)).map((r) => r.txn_id);
  const ticked = (kind, extra) => bulkSel.filter((id) => takers(kind, extra).includes(id));
  const bulk = g.bulk_answers || [];
  const clear = () => setBulkSel([]);
  const after = async (c) => { setBulkSel([]); await onDone(typeof c === "string" ? c : undefined); };

  // The rate pick: "all N rows" when the server offers it to every row of
  // the card, else for the ticked rows of this page.
  const rateRows = takers("PICK_RATE");
  const allRate = groupAnswer(g, "PICK_RATE");
  const rate = (allRate || rateRows.length > 0) && g.count > 0 && (
    <RateCategoryAction
      calcId={calcId} flagType={g.flag_type} groupCount={g.count} rateOptions={g.rate_options}
      allowAll={!!allRate} disabled={calcFrozen} onDone={after}
      selectedIds={ticked("PICK_RATE")} onClearSelection={clear}
      pageCount={rateRows.length}
      onSelectPage={() => { setBulkSel(rateRows); setShowRows(true); }}
    />
  );

  // The tick bar: keep (Family B) and the answers given to ticked rows.
  const keepRows = g.family === "B" ? takers("KEEP") : [];
  const decisions = [];
  const channelRows = ticked("CREDIT_NOTE_CHANNEL");
  const channelsOf = (ids) => rows.filter((r) => ids.includes(r.txn_id))
    .map((r) => (r.answers || []).find((a) => a.kind === "CREDIT_NOTE_CHANNEL")?.channels || []);
  // A1-107 (operator 2026-11-02: 6,356 credit notes, "Decide together 50 of
  // 6356" only): the card's own channel question gets one control with "all
  // N rows on this card" beside "only the rows I tick", like the rate pick;
  // the tick bar then carries no second channel picker.
  const channelAll = !calcFrozen ? groupAnswer(g, "CREDIT_NOTE_CHANNEL", { all_open: true }) : null;
  const channel = channelAll && (
    <RefundChannelAction calcId={calcId} flagType={g.flag_type} channelAll={channelAll}
      tickedIds={channelRows} tickedChannels={channelsOf(channelRows)}
      active={data?.refund_channel_active || []} disabled={calcFrozen} onDone={after}
      pageCount={takers("CREDIT_NOTE_CHANNEL").length} onClearSelection={clear}
      onSelectPage={() => { setBulkSel(takers("CREDIT_NOTE_CHANNEL")); setShowRows(true); }} />
  );
  if (channelRows.length && !channelAll) {
    const common = channelsOf(channelRows).reduce((acc, c) => acc.filter((x) => c.includes(x)));
    decisions.push(<RefundChannelResolver key="channel" hideChip tids={channelRows} calcId={calcId}
      calcFrozen={calcFrozen} acked={false} options={common}
      active={data?.refund_channel_active || []} onResolved={after} />);
  }
  [...new Set(bulk.concat(rows.flatMap((r) => r.answers || []))
    .filter((a) => a.kind === "PICK_COUNTRY").map((a) => a.field))].forEach((field) => {
    const ids = ticked("PICK_COUNTRY", { field });
    if (ids.length > 1) {
      decisions.push(<LocationCountryPicker key={`loc-${field}`} tids={ids} field={field}
        calcId={calcId} calcFrozen={calcFrozen} onResolved={after} />);
    }
  });
  const estRows = ticked("ESTABLISHMENT");
  if (estRows.length > 1) {
    decisions.push(<CustomerEstablishmentResolver key="est" tids={estRows} calcId={calcId}
      calcFrozen={calcFrozen} acked={false} onResolved={after} />);
  }
  // A1-103 step 4 — "leave out the copy" for the ticked rows
  const copyRows = ticked("LEAVE_OUT_COPY");
  if (copyRows.length > 1) {
    decisions.push(<LeaveOutCopy key="leave-out" tids={copyRows} calcId={calcId} calcFrozen={calcFrozen}
      onResolved={after} testId="leave-out-ticked"
      label={t("ui.review_answers.leave_out_ticked", { n: copyRows.length, defaultValue: "Leave out the {{n}} ticked copies" })} />);
  }
  // A1-104 — where the customer belongs / what was sold, for the ticked rows
  const countryRows = ticked("CUSTOMER_COUNTRY");
  if (countryRows.length > 1) {
    decisions.push(<CustomerCountry key="cc" tids={countryRows} calcId={calcId} calcFrozen={calcFrozen}
      onResolved={after} testId="cc-ticked"
      label={t("ui.review_answers.customer_open_many", { n: countryRows.length, defaultValue: "Where do the {{n}} ticked customers belong? →" })} />);
  }
  const soldRows = ticked("SOLD_AS");
  if (soldRows.length > 1) {
    decisions.push(<SoldAs key="sold" tids={soldRows} calcId={calcId} calcFrozen={calcFrozen} onResolved={after} />);
  }
  // A2-625 — "evidence held" for the ticked rows
  const evidenceRows = ticked("BUSINESS_EVIDENCE");
  if (evidenceRows.length > 1) {
    decisions.push(<BusinessEvidence key="bev" tids={evidenceRows} calcId={calcId} calcFrozen={calcFrozen}
      onResolved={after}
      label={t("ui.review_answers.evidence_open_many", { n: evidenceRows.length, defaultValue: "Keep the {{n}} ticked customers as businesses →" })} />);
  }
  // A2-586 — clear the earlier answer on the ticked rows
  const clearRows = ticked("CLEAR_DECISION");
  if (clearRows.length > 1) {
    decisions.push(<ClearDecision key="clear" tids={clearRows} calcId={calcId} calcFrozen={calcFrozen}
      onResolved={after}
      label={t("ui.review_answers.clear_open_many", { n: clearRows.length, defaultValue: "Clear the earlier answer on {{n}} rows →" })} />);
  }
  const barTakers = takers;
  const tickRows = [...new Set([...keepRows, ...barTakers("CREDIT_NOTE_CHANNEL"), ...barTakers("PICK_COUNTRY"),
    ...barTakers("ESTABLISHMENT"), ...barTakers("LEAVE_OUT_COPY"), ...barTakers("CUSTOMER_COUNTRY"), ...barTakers("SOLD_AS"),
    ...barTakers("BUSINESS_EVIDENCE"), ...barTakers("CLEAR_DECISION")])];
  const bar = !calcFrozen && !g.calc_level && tickRows.length > 1 && (
    <FamilyBBulkResolver
      flagType={g.flag_type} calcId={calcId} keepOffered={keepRows.length > 0}
      keepSelected={bulkSel.filter((x) => keepRows.includes(x))}
      selected={bulkSel.filter((x) => tickRows.includes(x))} total={tickRows.length} groupTotal={g.count}
      onSelectAll={() => { setBulkSel(tickRows); setShowRows(true); }} onClear={clear}
      decision={decisions.length ? <>{decisions}</> : null}
      allRowsChoice={!!allRate || !!groupAnswer(g, "LEAVE_OUT_COPY") || !!channelAll}
      onResolved={after}
    />
  );
  const letting = groupAnswer(g, "PROPERTY_LETTING") || takers("PROPERTY_LETTING").length > 0;
  // Every row is an exact copy not yet left out: one answer for all the open
  // ones, on every page (the server picks them; this page holds only some).
  const allCopies = groupAnswer(g, "LEAVE_OUT_COPY");
  const openCount = (g.count || 0) - (g.acknowledged_count || 0);
  const leaveOutAll = allCopies && openCount > 0 && (
    <div data-testid={`rg-leave-out-all-${g.flag_type}`} className="flex flex-wrap items-center gap-2">
      <LeaveOutCopy allOpen calcId={calcId} calcFrozen={calcFrozen} onResolved={after}
        testId="leave-out-all"
        label={t("ui.review_answers.leave_out_all", { n: openCount, defaultValue: "Leave out all {{n}} copies" })} />
      <span className="text-xs text-slate-600 dark:text-slate-300">
        {t("ui.review_answers.leave_out_hint", { defaultValue: "The first row of each stays in. Applied at the next re-run; until then “Keep this row as it is” undoes it." })}
      </span>
    </div>
  );

  // A1-104 — every open row of the card at once, each to its own VAT
  // number's country (the server resolves each; rows whose number names no
  // country are left for a country picked per row).
  const allCountry = groupAnswer(g, "CUSTOMER_COUNTRY");
  const countryAll = allCountry && openCount > 0 && (
    <div data-testid={`rg-customer-all-${g.flag_type}`} className="flex flex-wrap items-center gap-2">
      <CustomerCountry allOpen flagType={g.flag_type} calcId={calcId} calcFrozen={calcFrozen} onResolved={after}
        testId="cc-all"
        label={t("ui.review_answers.customer_open_all", { n: openCount, defaultValue: "Where do all {{n}} customers belong? →" })} />
    </div>
  );

  // A2-625 — "evidence held" for every open row of the card, all pages
  const allEvidence = groupAnswer(g, "BUSINESS_EVIDENCE");
  const evidenceAll = allEvidence && openCount > 0 && (
    <div data-testid={`rg-evidence-all-${g.flag_type}`} className="flex flex-wrap items-center gap-2">
      <BusinessEvidence allOpen flagType={g.flag_type} calcId={calcId} calcFrozen={calcFrozen} onResolved={after}
        label={t("ui.review_answers.evidence_open_all", { n: openCount, defaultValue: "Keep all {{n}} customers as businesses →" })} />
    </div>
  );

  // A2-586 — clear the earlier answer on every open row of the card
  const allClear = groupAnswer(g, "CLEAR_DECISION");
  const clearAll = allClear && openCount > 0 && (
    <div data-testid={`rg-clear-all-${g.flag_type}`} className="flex flex-wrap items-center gap-2">
      <ClearDecision allOpen calcId={calcId} calcFrozen={calcFrozen} onResolved={after}
        label={t("ui.review_answers.clear_open_all", { n: openCount, defaultValue: "Clear the earlier answer on all {{n}} rows →" })} />
    </div>
  );

  // the exchange-rate jump is drawn on the card itself, open or folded
  const whole = bulk.filter((a) => ["GO_TO", "FIX_IN_FILE", "NO_WAY_OUT"].includes(a.kind)
    && !(a.kind === "GO_TO" && a.target === "step2.fx"));
  return (
    <>
      {rate}
      {channel}
      {leaveOutAll}
      {countryAll}
      {evidenceAll}
      {clearAll}
      {groupAnswer(g, "ADD_REGISTRATION") && <AddRegistrationCta clientId={clientId} />}
      {groupAnswer(g, "ONLINE_EVENT_CHOICE") && (
        <OnlineEventVatChoiceCta g={g} clientId={clientId} calcFrozen={calcFrozen} />
      )}
      {whole.length > 0 && (
        <div data-testid={`rg-group-answers-${g.flag_type}`} className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-600 dark:text-slate-300">
            {t("ui.review_answers.whole_group", { n: g.count - (g.acknowledged_count || 0),
              defaultValue: "For all {{n}} rows:" })}
          </span>
          {whole.map((a) => (a.kind === "GO_TO"
            ? <GoToAnswer key={`goto-${a.target}`} answer={a} clientId={clientId} />
            : a.kind === "FIX_IN_FILE"
              ? <FixInFileAnswer key={`fix-${(a.columns || []).join()}`} answer={a} />
              : <NoWayOutAnswer key="none" />))}
        </div>
      )}
      {bar}
      {letting && (
        <PropertyLettingBulk g={g} calcId={calcId} calcFrozen={calcFrozen} sel={bulkSel} setSel={setBulkSel}
          onResolved={after} />
      )}
    </>
  );
};

export default GroupAnswers;
